type ViewerLocation = {
	country?: string | null;
	city?: string | null;
};

type DistanceUnit = "km" | "mi";

const IMPERIAL_COUNTRIES = new Set([
	"us",
	"usa",
	"united states",
	"united states of america",
	"u s",
	"u s a",
	"america",
	"gb",
	"uk",
	"great britain",
	"united kingdom",
	"england",
	"scotland",
	"wales",
	"northern ireland",
	"liberia",
	"myanmar",
]);

const normalizeCountryName = (value?: string | null): string =>
	(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^\p{L}\p{N}\s]/gu, " ")
		.replace(/\s+/g, " ")
		.trim();

const extractCountryFromAddress = (value?: string | null): string => {
	const cleaned = (value ?? "").trim();
	if (!cleaned) return "";
	const parts = cleaned
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	return parts.length ? parts[parts.length - 1] : cleaned;
};

export const getDistanceUnit = (viewerLocation?: ViewerLocation): DistanceUnit => {
	const normalizedCountry = normalizeCountryName(viewerLocation?.country);
	if (IMPERIAL_COUNTRIES.has(normalizedCountry)) {
		return "mi";
	}

	const normalizedCityCountry = normalizeCountryName(
		extractCountryFromAddress(viewerLocation?.city)
	);
	return IMPERIAL_COUNTRIES.has(normalizedCityCountry) ? "mi" : "km";
};

const toDisplayDistance = (distanceKm: number, unit: DistanceUnit): number =>
	unit === "mi" ? distanceKm * 0.621371 : distanceKm;

export const formatDistanceText = (
	distance: unknown,
	viewerLocation?: ViewerLocation
): string => {
	if (distance === null || distance === undefined) {
		return "Distance unavailable";
	}

	const numericDistance = Number(distance);
	if (!Number.isFinite(numericDistance) || numericDistance < 0) {
		return "Distance unavailable";
	}

	const unit = getDistanceUnit(viewerLocation);
	const displayDistance = toDisplayDistance(numericDistance, unit);

	if (!Number.isFinite(displayDistance) || displayDistance <= 0) {
		return "Distance unavailable";
	}

	if (displayDistance < 1) {
		return `<1 ${unit} away`;
	}

	return `${Math.round(displayDistance)} ${unit} away`;
};

// Public profile distances describe a privacy-preserving area, not a precise
// member location. Search controls continue to use the normal formatter.
export const formatPublicProfileDistanceText = (
	distance: unknown,
	viewerLocation?: ViewerLocation
): string => {
	const label = formatDistanceText(distance, viewerLocation);
	return label === "Distance unavailable" ? label : `Approx. ${label}`;
};

export const formatDistanceSettingText = (
	distance: unknown,
	viewerLocation?: ViewerLocation
): string => {
	const numericDistance = Number(distance);
	const safeDistance =
		Number.isFinite(numericDistance) && numericDistance > 0
			? Math.round(numericDistance)
			: 1;
	return `${safeDistance} ${getDistanceUnit(viewerLocation)}`;
};

const KNOWN_FALLBACK_COORDINATES: Array<[number, number]> = [
	[-37.820797, 144.959183],
	[-37.820797, 144.820797],
];

const COORDINATE_EPSILON = 0.00001;

const toValidNumber = (value: unknown): number | null => {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return null;
	return numeric;
};

const hasValidCoordinatePair = (
	lat: number | null,
	lng: number | null
): boolean => {
	if (lat === null || lng === null) return false;
	if (lat < -90 || lat > 90) return false;
	if (lng < -180 || lng > 180) return false;
	return true;
};

const isSamePair = (
	latA: number | null,
	lngA: number | null,
	latB: number | null,
	lngB: number | null
): boolean => {
	if (!hasValidCoordinatePair(latA, lngA) || !hasValidCoordinatePair(latB, lngB)) {
		return false;
	}
	return (
		Math.abs((latA as number) - (latB as number)) <= COORDINATE_EPSILON &&
		Math.abs((lngA as number) - (lngB as number)) <= COORDINATE_EPSILON
	);
};

const isKnownFallbackPair = (
	lat: number | null,
	lng: number | null
): boolean => {
	return KNOWN_FALLBACK_COORDINATES.some(([fallbackLat, fallbackLng]) =>
		isSamePair(lat, lng, fallbackLat, fallbackLng)
	);
};

const coordinateKey = (lat: number, lng: number): string =>
	`${lat.toFixed(5)},${lng.toFixed(5)}`;

const haversineKm = (
	lat1: number,
	lng1: number,
	lat2: number,
	lng2: number
): number => {
	const toRad = (degrees: number) => (degrees * Math.PI) / 180;
	const earthRadiusKm = 6371;
	const dLat = toRad(lat2 - lat1);
	const dLng = toRad(lng2 - lng1);
	const a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos(toRad(lat1)) *
			Math.cos(toRad(lat2)) *
			Math.sin(dLng / 2) *
			Math.sin(dLng / 2);
	return earthRadiusKm * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

type DistanceInputMatch = {
	lat?: unknown;
	lng?: unknown;
	distance?: unknown;
};

type NormalizeDistanceOptions = {
	discoveryLat: unknown;
	discoveryLng: unknown;
	originLat: unknown;
	originLng: unknown;
};

export const normalizeMatchDistances = <T extends DistanceInputMatch>(
	matches: T[],
	options: NormalizeDistanceOptions
): T[] => {
	if (!Array.isArray(matches) || !matches.length) return matches;
	const discoveryLat = toValidNumber(options.discoveryLat);
	const discoveryLng = toValidNumber(options.discoveryLng);
	const originLat = toValidNumber(options.originLat);
	const originLng = toValidNumber(options.originLng);

	const pairCounts = new Map<string, number>();
	matches.forEach((match) => {
		const matchLat = toValidNumber(match.lat);
		const matchLng = toValidNumber(match.lng);
		if (!hasValidCoordinatePair(matchLat, matchLng)) return;
		const key = coordinateKey(matchLat as number, matchLng as number);
		pairCounts.set(key, (pairCounts.get(key) ?? 0) + 1);
	});

	const normalized = matches.map((match, index) => {
		const matchLat = toValidNumber(match.lat);
		const matchLng = toValidNumber(match.lng);
		const hasValidMatchCoordinates = hasValidCoordinatePair(matchLat, matchLng);
		const key = hasValidMatchCoordinates
			? coordinateKey(matchLat as number, matchLng as number)
			: "";
		const pairCount = key ? pairCounts.get(key) ?? 0 : 0;
		const knownFallback = isKnownFallbackPair(matchLat, matchLng);
		const repeatedSyntheticPair =
			pairCount >= 3 &&
			(knownFallback || isSamePair(matchLat, matchLng, discoveryLat, discoveryLng));

		let resolvedDistance = -1;
		if (
			hasValidCoordinatePair(originLat, originLng) &&
			hasValidMatchCoordinates &&
			!knownFallback &&
			!repeatedSyntheticPair
		) {
			const computedDistance = haversineKm(
				originLat as number,
				originLng as number,
				matchLat as number,
				matchLng as number
			);
			if (Number.isFinite(computedDistance) && computedDistance >= 0) {
				resolvedDistance = computedDistance;
			}
		}

		return {
			index,
			hasDistance: resolvedDistance >= 0,
			match: {
				...match,
				distance: resolvedDistance,
			} as T,
		};
	});

	normalized.sort((a, b) => {
		if (a.hasDistance && b.hasDistance) {
			const distanceA = Number(a.match.distance);
			const distanceB = Number(b.match.distance);
			return distanceA - distanceB;
		}
		if (a.hasDistance) return -1;
		if (b.hasDistance) return 1;
		return a.index - b.index;
	});

	return normalized.map((item) => item.match);
};
